import { supabase } from './supabase'
import { writeActivity } from './activity'
import { checkinDueAt, checkinRuntimeMinutes } from './checkinDue'

// ── watchSessions ─────────────────────────────────────────────────────────────
// Backs "I'm watching this right now" (Jason 2026-09-19): a live, episode-level
// session distinct from the show-level 'watching' STATUS (which only fires once,
// when a show is started/resumed). Two jobs:
//   1. Push a "X is watching S·E of Y right now" to followers, via the SAME
//      writeActivity/notify-activity pipeline every other activity push uses —
//      no new push infra, just a new activity `type`.
//   2. Schedule a "did you finish it?" check-in ~runtime+buffer minutes out, via
//      the `watch_sessions` table a Netlify scheduled function sweeps.
// Ending a session (see endWatchSession) is separate and best-effort — it's called
// from useWatchlist's mark-watched path so a real mark closes the loop naturally.

// The check-in clock lives in checkinDue.js (one source; see its header for why).

// Start a live session for (show, season, episode). Reuses an existing OPEN
// session for the exact same episode instead of creating a duplicate (re-tapping
// the icon on the same episode is a no-op, not a second push/timer) — but a
// different episode of the same show always gets its own session, since binge
// nights are the normal case, not the exception.
//
// Takes `wl` (not just user/shareActivity) so the tap has a REAL consequence
// beyond the broadcast + timer (Jason 2026-09-20: "have it actually change the
// state instead of just indicating it"). A show still sitting at plan_to_watch
// gets promoted to watching — the same silent promotion `promoteFromPlan`
// already does for planning an episode into Tonight, reused here rather than
// invented twice. Silent on purpose: the watching_now activity below is
// already the visible announcement; this shouldn't ALSO fire a second
// 'watching' activity/streak bump for what is functionally one action.
// `runtime` (minutes) is the EPISODE's runtime when the caller has it; otherwise it is
// looked up from TMDB's season listing here, so every door (deck, Tonight, My List,
// show page) gets the same clock without each one fetching.
export async function startWatchingSession(wl, user, show, { season, episode, runtime }) {
  if (!user?.id || !show?.id || !season || !episode) return null

  const { data: existing } = await supabase
    .from('watch_sessions')
    .select('id')
    .eq('user_id', user.id).eq('show_id', show.id)
    .eq('season', season).eq('episode', episode)
    .is('ended_at', null)
    .limit(1)
  if (existing?.length) return existing[0].id

  const runtimeMinutes = checkinRuntimeMinutes({ episodeRuntime: runtime || await episodeRuntime(show.id, season, episode), showRuntime: show.episode_run_time?.[0] })
  const startedAt = new Date()
  const dueAt = checkinDueAt(startedAt, { episodeRuntime: runtimeMinutes })

  const { data, error } = await supabase.from('watch_sessions').insert({
    user_id: user.id, show_id: show.id, show_name: show.name, show_poster: show.poster_path || null,
    season, episode, runtime_minutes: runtimeMinutes,
    started_at: startedAt.toISOString(), due_at: dueAt.toISOString(),
  }).select('id').single()
  if (error) {
    // 23505 = lost the race against another near-simultaneous tap on the same
    // episode (watch_sessions_one_open_per_episode, added 2026-09-20 for this
    // exact gap) — the winner already fired the activity/promotion below, so
    // just hand back its session id instead of erroring or double-firing.
    if (error.code === '23505') {
      const { data: winner } = await supabase.from('watch_sessions').select('id')
        .eq('user_id', user.id).eq('show_id', show.id).eq('season', season).eq('episode', episode)
        .is('ended_at', null).limit(1)
      return winner?.[0]?.id || null
    }
    console.error('[watchSessions] start failed', error.message)
    return null
  }

  // Fire-and-forget: same activity/push pipeline as every other type, just a new
  // `watching_now` case in activityLabel/notify-activity (see activity.js).
  writeActivity(user.id, wl?.shareActivity, 'watching_now', show, { season, episode })
  wl?.promoteFromPlan?.(show.id)

  return data?.id || null
}

// Close any OPEN session(s) for a show — called after a real mark-watched, so a
// session that already resolved itself (the viewer marked it before the check-in
// even fired) doesn't sit around waiting to notify about something already done.
// Closes ALL open sessions for the show, not just the matching episode: marking
// any episode is a strong enough "I'm done with this sitting" signal, and leaving
// a stale sibling session (e.g. from switching episodes mid-session) open would
// otherwise fire a pointless check-in later.
export async function endWatchSession(userId, showId) {
  if (!userId || !showId) return
  try {
    await supabase.from('watch_sessions')
      .update({ ended_at: new Date().toISOString() })
      .eq('user_id', userId).eq('show_id', showId).is('ended_at', null)
  } catch (_) { /* best-effort; a stray open session just times out via the sweep's staleness cutoff */ }
}

// The episode's own runtime from TMDB's season listing; null when unknown or offline.
// Lazy import keeps this module cheap for callers that never start a session.
async function episodeRuntime(showId, season, episode) {
  try {
    const { tmdbGet } = await import('./tmdb')
    const data = await tmdbGet(`/tv/${showId}/season/${season}`)
    const ep = (data?.episodes || []).find(e => e.episode_number === episode)
    return ep?.runtime || null
  } catch { return null }
}
